/**
 * Reports API client — real backend CRUD under `/api2/reports/`, plus the
 * ai-services widget-execution and report-naming calls.
 *
 * A report is stored and retrieved whole as a single JSON document. The server
 * speaks `{ id, data, version, created_at, updated_at }`; the rest of the
 * frontend has always read `report.config`, so we map `data` <-> `config` at
 * this boundary and keep the frontend shape `{ id, config, version, created,
 * updated }`. The fetch interceptor attaches the Auth0 token (and a JSON
 * Content-Type) to `/api2` URLs automatically.
 */
import { ENDPOINT } from './meta.api';
import { AI_SERVICE_URL } from '@/config/constants';

const REPORTS = `${ENDPOINT}/reports`;

// server { id, data, version, created_at, updated_at } -> frontend shape
const fromServer = (r) => ({
    id: r.id,
    config: r.data ?? {},
    version: r.version,
    created: r.created_at,
    updated: r.updated_at,
});

/**
 * Error thrown by the report CRUD calls. Carries the HTTP `status` (so callers
 * can branch on 409) and the parsed response `body`. A 409 body is mapped to
 * `{ version, config }` — the current server state — so the conflict UX has
 * everything it needs without re-fetching.
 */
export class ReportApiError extends Error {
    constructor(status, body) {
        super(
            body?.message || body?.error || `Report request failed: ${status}`,
        );
        this.name = 'ReportApiError';
        this.status = status;
        this.body = body;
    }
}

// Read the JSON body even on error, so a 409 can surface { version, data }.
const parseBody = async (response) => {
    const text = await response.text();
    return text ? JSON.parse(text) : null;
};

export const fetchReports = async () => {
    const r = await fetch(`${REPORTS}/`, { method: 'GET' });
    if (!r.ok) throw new ReportApiError(r.status, await parseBody(r));
    const rows = await parseBody(r);
    return (rows ?? []).map(fromServer);
};

export const fetchReport = async (id) => {
    const r = await fetch(`${REPORTS}/${id}/`, { method: 'GET' });
    if (!r.ok) throw new ReportApiError(r.status, await parseBody(r));
    return fromServer(await parseBody(r));
};

export const createReport = async (config = {}) => {
    const r = await fetch(`${REPORTS}/`, {
        method: 'POST',
        body: JSON.stringify({ data: config }),
    });
    if (!r.ok) throw new ReportApiError(r.status, await parseBody(r));
    return fromServer(await parseBody(r)); // version === 1
};

/**
 * Overwrite a report's config with an optimistic-concurrency check. Resolves to
 * `{ id, config, version }` (the new server version) on success; throws
 * `ReportApiError(409, { version, config })` when `expectedVersion` is stale so
 * the saver can fire the conflict UX instead of clobbering.
 */
export const updateReport = async ({ id, config, expectedVersion }) => {
    const r = await fetch(`${REPORTS}/${id}/`, {
        method: 'PUT',
        body: JSON.stringify({
            data: config,
            expected_version: expectedVersion,
        }),
    });
    if (r.ok) {
        const { version } = await parseBody(r);
        return { id, config, version };
    }
    const body = await parseBody(r);
    // Map the 409 body's `data` -> `config` so callers see the frontend shape.
    throw new ReportApiError(
        r.status,
        body && 'data' in body
            ? { version: body.version, config: body.data }
            : body,
    );
};

export const deleteReport = async (id) => {
    const r = await fetch(`${REPORTS}/${id}/`, { method: 'DELETE' });
    if (!r.ok) throw new ReportApiError(r.status, await parseBody(r));
};

export const duplicateReport = async (id) => {
    const r = await fetch(`${REPORTS}/${id}/duplicate/`, { method: 'POST' });
    if (!r.ok) throw new ReportApiError(r.status, await parseBody(r));
    return fromServer(await parseBody(r));
};

// ---------------------------------------------------------------------------
// Widget execution — a REAL ai-services call (a different host).
// ---------------------------------------------------------------------------

/**
 * Run one stored widget config (resolved get_entity_data kwargs) and return the
 * tool output the inline visuals consume. Hits the non-streaming ai-services
 * endpoint; the fetch interceptor attaches the Auth0 token. Fetched fresh on
 * each report view so a returning user sees the latest data.
 */
export const executeWidget = async (args) => {
    const r = await fetch(`${AI_SERVICE_URL}/ai/reports/widget/`, {
        method: 'POST',
        body: JSON.stringify(args),
    });
    if (!r.ok) {
        const data = await r.json().catch(() => null);
        throw new Error(data?.error || `Failed to load widget: ${r.status}`);
    }
    const data = await r.json();
    // The tool reports its own failures with success:false (HTTP 200); surface
    // those as errors so the widget tile shows an error state.
    if (!data?.success) {
        throw new Error(data?.error || 'Widget data unavailable');
    }
    return data;
};

/**
 * Generate a report title from the user's request. Hits the non-streaming
 * naming endpoint; the fetch interceptor attaches the Auth0 token. Called once
 * at report-create time so the title stays fixed as the data changes week to
 * week (unlike the analysis, which re-streams per data hash via the synthesize
 * endpoint + useCompletion in ReportView). Returns { title }.
 */
export const nameReport = async ({ prompt }) => {
    const r = await fetch(`${AI_SERVICE_URL}/ai/reports/name/`, {
        method: 'POST',
        body: JSON.stringify({ prompt }),
    });
    if (!r.ok) {
        const data = await r.json().catch(() => null);
        throw new Error(data?.error || `Failed to name report: ${r.status}`);
    }
    return r.json();
};

// Report analysis is streamed from `/ai/reports/synthesize/` (plain text) and
// consumed directly by `useCompletion` in ReportView — no bespoke client here.
